//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { useDispatch } from "react-redux";
import { createFragmentContainer, RelayProp } from "react-relay";
import { makeStyles } from "@material-ui/core";
// event-category-coloring imports
import { eventCategoryColoringFragments } from "./EventCategoryColoring.gql";
// other components
import { CalendarColorPicker } from "@components/calendar-color-picker";
// util
import { logger } from "@util/logger.util";
import { getCategoryLabels, isValidCategory } from "@util/event-category-coloring.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { weight } from "@clockwise/web-commons/src/styles/type.styles";
import { colors } from "@clockwise/web-commons/src/styles/color.styles";
// state
import { updateEventColoringSettings } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";

//////////////////
// TYPES
//////////////////
type EventColoringSettingsCmptProps = {
  relay: RelayProp;
  org: ISchema.IOrg;
  eventCategoryColoring: ISchema.IEventCategoryColoring;
};

//////////////////
// STYLES
//////////////////
const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  category__label: {
    color: "black",
    fontSize: "0.875rem",
    marginBottom: 5,
  },
  category__description: {
    fontSize: "0.75rem",
    marginBottom: 10,
    "& > a": {
      color: colors.green.standard,
      textDecoration: "underline",
      marginLeft: 4,
      fontWeight: weight.std,
    },
  },
});

//////////////////
// COMPONENT
//////////////////
const EventCategoryColoringCmpt = ({
  relay,
  org,
  eventCategoryColoring,
}: EventColoringSettingsCmptProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const onError = (error: string) => {
    logger.error("updateEventColoringSettings failed", error);
    const eMsg = "Color selection failed to save. Please try again.";
    dispatch(snackBarOpen(eMsg));
  };

  const onSuccess = () => {
    const msg = "Saved. Your event colors will update shortly.";
    dispatch(snackBarOpen(msg));
  };

  const onSelectColor = (key: string | null) => {
    const active = !!key;
    const googleColorKey = !!key ? key : eventCategoryColoring.googleColorKey;

    // new coloring settings
    const updatedColoring: ISchema.IEventCategoryColoring = {
      ...eventCategoryColoring,
      googleColorKey,
      active,
    };

    if (active) {
      track(TrackingEvents.COLOR_CATEGORIES.SETTINGS_ENABLED, {
        ...updatedColoring,
      });
    } else {
      track(TrackingEvents.COLOR_CATEGORIES.SETTINGS_DISABLED, {
        ...updatedColoring,
      });
    }

    // input for mutation
    const input = {
      orgRelayId: org.id,
      eventCategoryColorings: [updatedColoring],
    };

    // fire off the mutation
    updateEventColoringSettings(relay.environment, input, onSuccess, onError);
  };

  const { eventCategory, googleColorKey, active } = eventCategoryColoring;

  if (!isValidCategory(eventCategory)) {
    logger.warn(`front-end cannot translate eventCategory ${eventCategory}`);
    return <span />;
  }

  const labels = getCategoryLabels(eventCategory);

  return (
    <div className={classes.container}>
      <div>
        <div className={classes.category__label}>{labels.label}</div>
        <div className={classes.category__description}>{labels.description}</div>
      </div>
      <CalendarColorPicker
        active={active}
        selectedColorIndex={googleColorKey}
        onSelect={onSelectColor}
      />
    </div>
  );
};

//////////////////
// RELAY
//////////////////
export const EventCategoryColoring = createFragmentContainer(
  EventCategoryColoringCmpt,
  eventCategoryColoringFragments,
);
